import React, { useReducer, createContext } from "react";

import {
  fundraiserOrUndefined,
  getFundraiser,
} from "src/context/FundraisersContext/selectors";
import {
  FundraisersState,
  FundraiserAction,
  FundraiserFetchStatus,
  FundraiserActionType,
  FundraiserIdentifier,
  InternalFundraisersState,
  FundraiserInput,
  ContextFundraiser,
} from "src/context/FundraisersContext/types";
import { logger } from "src/utility/logger";

const INITIAL_FUNDRAISER_STATE: Omit<
  FundraisersState,
  "dispatchFundraiserAction"
> = {
  fundraisersById: new Map(),
  fundraiserFetchStatus: new Map(),
};

/**
 * Add fundraisers data to the context.
 */
export function addFundraisers(fundraisers: FundraiserInput[]) {
  if (!dispatchFundraisersAction) {
    logger.warn({ message: "Dispatch is undefined for FundraisersContext." });
  }
  dispatchFundraisersAction({
    type: FundraiserActionType.ADD_FUNDRAISERS,
    data: fundraisers,
  });
}

function updateStateWithArrayOfFundraisers(
  state: InternalFundraisersState,
  fundraisers: FundraiserInput[]
): InternalFundraisersState {
  // Merge incoming fundraisers with existing data to preserve full data
  // when light feed responses are added. Cast is safe due to merging logic.
  const fundraisersById = new Map(state.fundraisersById);
  for (const fundraiser of fundraisers) {
    const existing = fundraisersById.get(fundraiser.id);
    fundraisersById.set(fundraiser.id, {
      ...existing,
      ...fundraiser,
    } as ContextFundraiser);
  }

  const incomingFundraisersFetchStatus = new Map();

  fundraisers.forEach(({ id }) => {
    incomingFundraisersFetchStatus.set(id, FundraiserFetchStatus.FOUND);
  });

  const fundraiserFetchStatus = new Map([
    ...state.fundraiserFetchStatus,
    ...incomingFundraisersFetchStatus,
  ]);

  return { ...state, fundraisersById, fundraiserFetchStatus };
}

/**
 * Update fetched status of a fundraiser currently being fetched.
 */
function updateStateWithStatus(
  state: InternalFundraisersState,
  identifier: FundraiserIdentifier,
  status: FundraiserFetchStatus
): InternalFundraisersState {
  const { fundraiserFetchStatus: prevFundraiserFetchStatus, ...rest } = state;
  const fundraiser = fundraiserOrUndefined(getFundraiser(state, identifier));
  if (fundraiser) {
    return state;
  }
  const fundraiserFetchStatus = new Map(prevFundraiserFetchStatus);
  fundraiserFetchStatus.set(identifier.id, status);
  return {
    ...rest,
    fundraiserFetchStatus,
  };
}

/**
 * Adds a fundraiser to the store of fundraiser, when one has been successfully
 * fetched. Merges with existing data to preserve full data when light feed
 * responses are added. Cast is safe due to merging logic.
 */
function updateStateWithFundraiser(params: {
  state: InternalFundraisersState;
  fundraiser: FundraiserInput;
}): InternalFundraisersState {
  const { state, fundraiser } = params;
  const fundraisersById = new Map(state.fundraisersById);
  const fundraiserFetchStatus = new Map(state.fundraiserFetchStatus);

  const existing = fundraisersById.get(fundraiser.id);
  fundraisersById.set(fundraiser.id, {
    ...existing,
    ...fundraiser,
  } as ContextFundraiser);
  fundraiserFetchStatus.set(fundraiser.id, FundraiserFetchStatus.FOUND);

  return {
    fundraisersById,
    fundraiserFetchStatus,
  };
}

function fundraisersReducer(
  state: InternalFundraisersState,
  action: FundraiserAction
): InternalFundraisersState {
  switch (action.type) {
    case FundraiserActionType.FETCHING_FUNDRAISER:
      return updateStateWithStatus(
        state,
        action.data,
        FundraiserFetchStatus.FETCHING_FUNDRAISER
      );
    case FundraiserActionType.FUNDRAISER_NOT_FOUND:
      return updateStateWithStatus(
        state,
        action.data,
        FundraiserFetchStatus.FUNDRAISER_NOT_FOUND
      );
    case FundraiserActionType.ADD_FUNDRAISER:
      return updateStateWithFundraiser({
        state: state,
        fundraiser: action.data,
      });
    case FundraiserActionType.ADD_FUNDRAISERS:
      return updateStateWithArrayOfFundraisers(state, action.data);
    default:
      throw new Error(`Fundraiser action with unknown type: ${action}`);
  }
}

export let dispatchFundraisersAction: React.Dispatch<FundraiserAction>;

export const FundraisersContext = createContext<FundraisersState>(
  // technically this is unsound since `dispatchFundraisersAction` will not be present,
  // but we always provide it in the provider below so for ergonomics we take
  // this risk
  INITIAL_FUNDRAISER_STATE as FundraisersState
);

export const FundraisersProvider: React.FCC<{
  initialData?: FundraiserInput[];
}> = ({ children, initialData }) => {
  const [fundraisersState, fundraisersDispatcher] = useReducer(
    fundraisersReducer,
    initialData
      ? updateStateWithArrayOfFundraisers(INITIAL_FUNDRAISER_STATE, initialData)
      : INITIAL_FUNDRAISER_STATE
  );

  dispatchFundraisersAction = fundraisersDispatcher;
  return (
    <FundraisersContext.Provider
      value={{
        ...fundraisersState,
        dispatchFundraiserAction: fundraisersDispatcher,
      }}
    >
      {children}
    </FundraisersContext.Provider>
  );
};
